import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';

export const Content = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  gap: 36px;
  position: relative;
  margin-bottom: 20px;
  //margin-top: 20px;
  overflow: hidden;

  /* Media Queries for Content */

  // For screens smaller than 1200px
  @media (max-width: 1200px) {
    width: 100%;
    //gap: 24px;
    padding: 32px;
  }

  // For screens smaller than 768px
  @media (max-width: 768px) {
    width: 100%;
    //gap: 16px;
    margin-top: 10px;
    margin-bottom: 10px;
    padding: 16px;
    overflow-x: hidden;
  }

  // For very small screens
  @media (max-width: 480px) {
    padding: 10px;
    //gap: 10px;
  }
`;

export const LeftContent = styled('div')`
  display: flex;
  width: 100%;
  padding: 32px 64px;
  justify-content: space-between;
  align-items: center;
  gap: 42px;
  flex-direction: row;
  border-radius: 16px;
  box-sizing: border-box;
  min-height: 200px;
  color: ${({ theme }) => theme.palette.text.primary};

  @media (max-width: 1400px) {
    flex-direction: column;
    align-items: stretch;
    gap: 24px;
    padding: 24px 32px;
  }

  @media (max-width: 700px) {
    padding: 20px 16px;
    gap: 16px;
  }

  border-bottom: 5px solid
    ${({ theme }) =>
      theme.palette.mode === 'dark'
        ? theme.palette.transparent.main
        : theme.palette.grey['300'] || theme.palette.transparent.secondary};

  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);

  border: ${({ theme }) =>
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(0, 0, 0, 0.05)'};

  box-shadow: ${({ theme }) =>
    theme.palette.mode === 'dark'
      ? '0 8px 24px rgba(0, 207, 255, 0.12)'
      : '0 8px 24px rgba(0, 0, 0, 0.06)'};

  background: ${({ theme }) =>
    theme.palette.mode === 'dark'
      ? 'linear-gradient(145deg, rgba(28, 34, 48, 0.85) 0%, rgba(0, 207, 255, 0.12) 100%)'
      : 'linear-gradient(145deg, rgba(255, 255, 255, 0.7) 0%, rgba(243, 246, 251, 0.9) 100%)'};
`;

export const RouterIcon = styled('div')(
  ({ theme }) => `
  .path {
    fill: ${theme.palette.white.main};
    border: 1px solid ${theme.palette.grey['04']};
  }
`
);

export const RouterName = styled('h3')(
  ({ theme }) => `
  color: ${theme.palette.primary.main};
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 2px;
`
);

export const RouterId = styled('p')(
  ({ theme }) => `
  color: ${theme.palette.primary.main};
  font-weight: 400;
  font-size: 16px;
  margin-top: 2px;
`
);

export const TagButton = styled('div')(
  ({ theme }) => `
  display: flex;
  align-items: center;
  background-color: ${theme.palette.primary['01']};
  color: ${theme.palette.white.main};
  border-radius: 32px;
  height: 36px;
  padding-right: 12px;
`
);

export const IconTag = styled('div')(
  ({ theme }) => `
  background-color: ${theme.palette.primary['04']};
  color: ${theme.palette.white.main};
  font-size: 10px;
  font-weight: 700;
  border-radius: 32px;
  height: 36px;
  align-items: center;
  display: flex;
  padding-right: 12px;
  padding-left: 12px;
`
);

export const TextTag = styled('span')`
  display: flex;
  justify-content: center;
  width: 100%;
`;

export const RightContent = styled('div')`
  display: grid;
  /* minmax(0, 1fr) keeps wide children from stretching the tracks past the viewport */
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 32px;
  width: 100%;
  max-width: 100%;
  height: auto;

  /* Media Queries for RightContent */

  // For screens smaller than 1200px
  @media (max-width: 1200px) {
    grid-template-columns: minmax(0, 1fr); /* Stack items vertically */
    //gap: 24px;
  }

  // For screens smaller than 768px
  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
    //gap: 16px;
  }
`;

interface BadgeProps {
  modeView?: string;
  fullWidth?: boolean;
  wide?: boolean;
}
const gridLayout = (columns: string, span: string) => `
      display: grid;
      grid-template-columns: ${columns};
      gap: 16px;
      overflow: visible;

      & > :first-child {
        grid-column: 1;
        max-width: 100%;
        align-self: start;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      & > :last-child {
        grid-column: ${span};
        max-width: 100%;
        overflow-wrap: break-word;
        word-break: break-word;
        white-space: normal;
        overflow: visible;
      }
    `;

export const InfoBadge = styled('div')<BadgeProps>`
  display: flex;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.white['06']};
  gap: 12px;
  text-overflow: ellipsis;
  overflow-x: hidden;
  min-width: 0;

  ${({ modeView }) =>
    modeView !== 'grid'
      ? `
        && > * {
          white-space: normal;
          overflow-wrap: anywhere;
          word-break: break-word;
          text-overflow: clip;
        }
      `
      : ''}

  ${({ fullWidth, wide, modeView }) => {
    if (fullWidth)
      return `
        grid-column: 1 / -1;
        ${modeView === 'grid' && gridLayout('repeat(6, 1fr)', '2 / -1')}
      `;
    if (wide && modeView === 'grid')
      return `
        grid-column: 1 / 3;
        ${gridLayout('repeat(4, 1fr)', '2 / -1')}
      `;
    return '';
  }}
`;

export const Info = styled('div')`
  color: ${({ theme }) => theme.palette.text.primary};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  min-width: 0;

  & > svg {
    flex-shrink: 0;
  }
`;

export const BadgeTitle = styled('div')<BadgeProps>`
  color: ${({ theme }) => theme.palette.grey['01']};
  max-width: ${({ modeView }) => (modeView === 'grid' ? '122px' : '240px')};
  overflow-x: hidden;
  width: 100%;
  min-width: 0;
  justify-content: flex-end;
  white-space: nowrap;
  text-align: right;
  text-overflow: ellipsis;
`;

interface BodyProps {
  modeView?: boolean;
}

interface BadgeValueProps {
  empty?: boolean;
  modeView?: string;
  fullWidth?: boolean;
}

export const BadgeValue = styled('div')<BadgeValueProps>`
  overflow-x: hidden;
  max-width: ${({ modeView }) => (modeView === 'grid' ? '122px' : '100%')};
  width: 100%;
  min-width: 0;
  white-space: ${({ fullWidth }) => (fullWidth ? 'normal' : 'nowrap')};
  text-overflow: ellipsis;
  color: ${({ theme, empty }) =>
    empty ? theme.palette.grey['05'] : theme.palette.text.primary};
  text-align: left;
`;

export const CardBody = styled('div')<BodyProps>`
  display: grid;
  //flex-direction: ${(props) => (props.modeView ? 'row' : 'column')};
  grid-template-columns: ${(props) =>
    props.modeView ? 'repeat(3, minmax(0, 1fr))' : 'minmax(0, 1fr)'};
  width: 100%;
  flex-wrap: wrap;
  gap: 16px;
  position: relative;
  //padding: 24px;

  max-width: 100%;
  box-sizing: border-box;
`;

export const ResumeGrid = styled('div')<{ isExpanded?: boolean }>`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: repeat(4, auto);
  gap: 8px;
  margin-top: 8px;
  width: 100%;
  max-width: 500px;

  @media (max-width: 1400px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto;
    max-height: ${({ isExpanded }) => (isExpanded ? 'auto' : '100px')};
    overflow-y: hidden;
  }
`;

export const LoadBar = styled('div')`
  height: 4px;
  width: 100px;
  background-color: currentColor;
  border-radius: 48px;
  margin-left: 12px;
  margin-right: 12px;
`;

export const NoDataContainer = styled(Stack)`
  width: 100%;
  align-items: center;
  justify-content: center;
  margin: 40px auto;
`;
